// Render a filterable, grouped SKU table from Shopify product data via FFCart.
// Usage:
//   renderSkuTable({
//     handle: 'flush-fastener-metric-countersunk',
//     mount: '#skuTable',
//     chips: '#sizeChips',
//     visCountEl: '#visCount',
//     totalCountEl: '#totalCount',
//   });
//
// This replaces the old static skus.js flow. Rows are built from real Shopify
// variants (ids, SKUs, option labels, prices), so Buy buttons always work and
// prices always reflect the live storefront.
//
// If FFCart is not yet ready, we wait for 'ffcart:ready' before rendering.

function renderSkuTable(opts) {
  const mount = document.querySelector(opts.mount);
  const chipsEl = document.querySelector(opts.chips);
  const visCountEl = document.querySelector(opts.visCountEl);
  const totalCountEl = document.querySelector(opts.totalCountEl);
  let activeSize = 'all';

  function fmtPriceCents(cents) {
    if (!cents || cents <= 0) return '<span class="price tbd">Contact for price</span>';
    return '<span class="price">$' + (cents / 100).toFixed(2) + '<span class="price-unit"> / ea · sold in packs of 10</span></span>';
  }

  // ----- Downloads (STEP + drawing PDF + DWG) -----
  // Each part number has up to three downloadable files hosted on Shopify Files
  // (or locally during dev). Filenames follow the Inventor-export convention:
  // spaces between size groups, ".ipt." suffix on STEP, no ".ipt" on PDF/DWG.
  // Shopify converts spaces to underscores on upload, so the canonical URL is:
  //   {base}FF-{size}_C_{length}_MCT-SB.ipt.{ext}     (STEP)
  //   {base}FF-{size}_C_{length}_MCT-SB.{ext}         (PDF, DWG)
  // Shopify CDN URLs include a unique ?v={hash} per file that we can't construct
  // in JS. Instead, the Liquid template emits window.FF_FILE_URLS[filename] =
  // 'https://cdn.shopify.com/.../filename.ext?v=...' for every uploaded CAD file.
  // We just look up the URL by constructed filename. If not present, the file
  // doesn't exist — disable the button rather than 404.
  function buildDownloadUrl(partNumber, kind) {
    let filename;
    if (kind === 'drawing') {
      filename = partNumber + '.pdf';
    } else if (kind === 'dwg') {
      filename = partNumber + '.dwg';
    } else {
      // STEP files keep the .ipt segment from Inventor export.
      filename = partNumber + '.ipt.step';
    }
    const map = window.FF_FILE_URLS || {};
    const url = map[filename];
    if (!url) return null;
    // Shopify's file_url filter returns a placeholder URL with a tiny ?v=N counter
    // (e.g. ?v=1323) for files that don't actually exist. Real uploaded files have
    // 18+ digit version hashes. Treat short hashes as "missing".
    const m = url.match(/[?&]v=(\d+)/);
    if (m && m[1].length < 10) return null;
    return url;
  }

  function dlButton(label, partNumber, kind) {
    const href = buildDownloadUrl(partNumber, kind);
    if (!href) {
      return `<span class="dl dl-missing" title="File not yet available">${label}</span>`;
    }
    return `<a class="dl" href="${href}" download>${label}</a>`;
  }

  // ----- Part-number generation -----
  // Converts Shopify option values (e.g. "M3.5-0.6" / "7mm", "6-32" / "1/4\"") into Flush Fasteners part codes.
  //   Metric : FF-m{thread}C{lenMM}CT-SB        e.g. M3.5 × 7mm  → FF-m35C07CT-SB
  //   Imperial: FF-{threadCode}C{lenHundredths}MCT-SB
  //              where threadCode = major-diameter inches × 100 (#6→14, #8→16, #10→19, 1/4→25, 5/16→31, 3/8→38, 1/2→50)

  const IMPERIAL_THREAD_CODES = {
    '6-32': '14',
    '8-32': '16',
    '10-24': '19',
    '10-32': '19',
    '1/4-20': '25',
    '5/16-18': '31',
    '3/8-16': '38',
    '1/2-13': '50',
  };

  function parseInchLen(s) {
    // "1/4\"" → 0.25, "3/8\"" → 0.375, "1 1/8\"" → 1.125, "1\"" → 1.0
    const clean = s.replace(/["']/g, '').trim();
    const mixed = clean.match(/^(\d+)\s+(\d+)\/(\d+)$/);
    if (mixed) return parseInt(mixed[1], 10) + parseInt(mixed[2], 10) / parseInt(mixed[3], 10);
    const frac = clean.match(/^(\d+)\/(\d+)$/);
    if (frac) return parseInt(frac[1], 10) / parseInt(frac[2], 10);
    const whole = parseFloat(clean);
    return isNaN(whole) ? 0 : whole;
  }

  function partNumberFor(size, length, isMetric) {
    if (isMetric) {
      // size like "M3.5-0.6" → thread token "3.5"; "M4-0.7" → "4.0"; "M10-1.5" → "10"
      // CDN naming rule: single-digit threads get .0 suffix (m3.0, m4.0, m5.0, m6.0, m8.0)
      // Double-digit threads stay whole (m10, m12, m14, m16)
      let thread = (size.match(/^M([\d.]+)/) || [,''])[1];
      const lenMM = (length.match(/([\d.]+)/) || [,''])[1];
      if (!thread || !lenMM) return '';
      // If thread is a whole number AND single digit (< 10), append .0
      if (!thread.includes('.')) {
        const n = parseInt(thread, 10);
        if (n < 10) thread = thread + '.0';
      }
      return `FF-m${thread}_C_${lenMM}_MCT-SB`;
    } else {
      const code = IMPERIAL_THREAD_CODES[size.trim()] || '';
      if (!code) return '';
      const inches = parseInchLen(length);
      if (!inches) return '';
      // CDN filename pattern: FF-14_C_038_MCT-SB.dwg (countersunk uses _C_ for both metric AND imperial)
      // 0.37" → 037, 1.00" → 100, 1.75" → 175 (hundredths, 3 digits)
      const hundredths = Math.round(inches * 100);
      const lenPad = String(hundredths).padStart(3, '0');
      return `FF-${code}_C_${lenPad}_MCT-SB`;
    }
  }

  // ----- Display formatting -----
  function displaySize(size, isMetric) {
    if (isMetric) {
      // "M3.5-0.6" → "M3.5" (drop thread pitch for the table column; pitch is standard per size)
      const m = size.match(/^(M[\d.]+)/);
      return m ? m[1] : size;
    }
    // Imperial: "6-32" → "#6 (6-32)", "1/4-20" → "¼″ (1/4-20)" — keep readable
    const imperialMap = {
      '6-32': '#6', '8-32': '#8', '10-24': '#10', '10-32': '#10',
      '1/4-20': '¼″', '5/16-18': '⁵⁄₁₆″', '3/8-16': '⅜″', '1/2-13': '½″',
    };
    return imperialMap[size.trim()] || size;
  }

  function displayLength(length, isMetric) {
    if (isMetric) {
      // "7mm" → "7 mm"
      return length.replace(/(\d)\s*mm/i, '$1 mm');
    }
    // Imperial: leave "1/4"", "3/8"", "1 1/8"" as-is but normalize quotes
    return length.replace(/"/g, '″');
  }

  function buyButton(variantId, sku) {
    if (variantId) {
      return `<button class="buy" type="button" data-ffcart-vid="${variantId}" data-ffcart-sku="${sku || ''}">Buy →</button>`;
    }
    return `<span class="buy disabled" aria-hidden="true"></span>`;
  }

  // Build rows from a Shopify product JSON. Returns: [{size, length, price, variantId, sku, partNumber}]
  function rowsFromProduct(product) {
    if (!product || !product.variants) return [];
    // Infer metric vs imperial from the product handle
    const isMetric = /metric/i.test(product.handle || '');
    return product.variants.map(v => {
      const size = v.option1 || '';
      const length = v.option2 || '';
      const generatedPart = partNumberFor(size, length, isMetric);
      return {
        size,
        length,
        displaySize: displaySize(size, isMetric),
        displayLength: displayLength(length, isMetric),
        price: v.price || 0,
        variantId: v.id,
        sku: v.sku || '',
        // Prefer the FF-style generated part code; if it can't be derived, fall back to the Shopify SKU.
        partNumber: generatedPart || v.sku || v.title || '',
        available: v.available !== false,
      };
    });
  }

  function sortRows(rows) {
    // Sort by size first (numerically), then by length within each size.
    const lengthKey = (l) => {
      const clean = String(l).replace(/["']/g, '').trim();
      // Metric: "7mm" or "7 mm" → 7
      const mm = clean.match(/^([\d.]+)\s*mm$/i);
      if (mm) return parseFloat(mm[1]);
      // Imperial mixed: "1 1/8" → 1.125
      const mixed = clean.match(/^(\d+)\s+(\d+)\/(\d+)$/);
      if (mixed) return parseInt(mixed[1], 10) + parseInt(mixed[2], 10) / parseInt(mixed[3], 10);
      // Imperial fraction: "3/8" → 0.375
      const frac = clean.match(/^(\d+)\/(\d+)$/);
      if (frac) return parseInt(frac[1], 10) / parseInt(frac[2], 10);
      // Imperial whole: "1" → 1
      const whole = parseFloat(clean);
      return isNaN(whole) ? 0 : whole;
    };
    const sizeKey = (s) => {
      // Metric: "M3.5-0.6" → 3.5, "M10-1.5" → 10
      const metric = s.match(/^M([\d.]+)/i);
      if (metric) return parseFloat(metric[1]);
      // Imperial: "6-32" → 0.138, "1/4-20" → 0.25, "5/16-18" → 0.3125
      const numCodes = { '6': 0.138, '8': 0.164, '10': 0.190 };
      const num = s.match(/^(\d+)-/);
      if (num && numCodes[num[1]]) return numCodes[num[1]];
      const frac = s.match(/^(\d+)\/(\d+)/);
      if (frac) return parseFloat(frac[1]) / parseFloat(frac[2]);
      return 0;
    };
    return rows.sort((a, b) => {
      const sd = sizeKey(a.size) - sizeKey(b.size);
      if (sd !== 0) return sd;
      return lengthKey(a.length) - lengthKey(b.length);
    });
  }

  function render(product) {
    const allRows = sortRows(rowsFromProduct(product));
    const sizes = [];
    allRows.forEach(r => { if (!sizes.includes(r.size)) sizes.push(r.size); });

    const filtered = activeSize === 'all' ? allRows : allRows.filter(r => r.size === activeSize);

    // Group by size
    const bySize = {};
    filtered.forEach(r => { (bySize[r.size] = bySize[r.size] || []).push(r); });

    let html = '';
    html += `<div class="sku-row header">
      <div>Part number</div>
      <div>Size</div>
      <div>Length</div>
      <div>Price</div>
      <div>Downloads</div>
      <div></div>
    </div>`;

    Object.keys(bySize).forEach(size => {
      const group = bySize[size];
      const sizeLabel = group[0].displaySize || size;
      html += `<div class="size-divider">${sizeLabel} <span class="count">${group.length} lengths</span></div>`;
      group.forEach(r => {
        html += `<div class="sku-row${r.available ? '' : ' oos'}">
          <div class="part">${r.partNumber}</div>
          <div class="size">${r.displaySize || r.size}</div>
          <div class="length">${r.displayLength || r.length}</div>
          <div>${fmtPriceCents(r.price)}</div>
          <div class="downloads">
            ${dlButton('Drawing · PDF', r.partNumber, 'drawing')}
            ${dlButton('STEP', r.partNumber, 'step')}
            ${dlButton('DWG', r.partNumber, 'dwg')}
          </div>
          <div>${r.available ? buyButton(r.variantId, r.sku) : '<span class="buy oos-label" aria-hidden="false">Out of stock</span>'}</div>
        </div>`;
      });
    });

    mount.innerHTML = html;
    if (visCountEl) visCountEl.textContent = filtered.length;
    if (totalCountEl) totalCountEl.textContent = allRows.length;

    // Download buttons are rendered as direct <a> tags by dlButton() — no hydration needed.

    // Wire Buy buttons — each click adds a pack of 10
    mount.querySelectorAll('[data-ffcart-vid]').forEach(btn => {
      btn.addEventListener('click', async () => {
        if (!window.FFCart) return;
        const vid = parseInt(btn.dataset.ffcartVid, 10);
        const sku = btn.dataset.ffcartSku;
        btn.disabled = true;
        const prev = btn.textContent;
        btn.textContent = 'Adding…';
        const r = await window.FFCart.addVariant(vid, 10, { sku });
        btn.disabled = false;
        btn.textContent = r.ok ? 'Added ✓' : 'Try again';
        setTimeout(() => { btn.textContent = prev; }, 1400);
      });
    });

    // Build chips (size filter) — use the first row's displaySize per bucket
    const sizeLabels = {};
    allRows.forEach(r => { if (!sizeLabels[r.size]) sizeLabels[r.size] = r.displaySize || r.size; });
    let chipHtml = `<button class="chip ${activeSize === 'all' ? 'active' : ''}" data-size="all">All sizes</button>`;
    sizes.forEach(s => {
      chipHtml += `<button class="chip ${activeSize === s ? 'active' : ''}" data-size="${s}">${sizeLabels[s]}</button>`;
    });
    chipsEl.innerHTML = chipHtml;
    chipsEl.onclick = (e) => {
      const btn = e.target.closest('.chip');
      if (!btn) return;
      activeSize = btn.dataset.size;
      render(product);
    };
  }

  function tryRender() {
    const product = window.FFCart && window.FFCart.getProduct && window.FFCart.getProduct(opts.handle);
    if (product) {
      render(product);
      return true;
    }
    return false;
  }

  if (!tryRender()) {
    // Show loading skeleton
    mount.innerHTML = `<div class="sku-loading">Loading catalog from flushfasteners.com…</div>`;
    chipsEl.innerHTML = '';
    document.addEventListener('ffcart:ready', () => tryRender(), { once: true });
  }
}
